import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import MaskedInput from '../common/MaskedInput';
import { login } from '../api/auth';
import { useTranslation } from '../context/TranslationContext';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import styles from './auth.module.css';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await login(username, password);
      navigate(from, { replace: true });
    } catch (err) {
      const msg = err.customMessage || err.response?.data?.message || t('error');
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.row}>
        <aside className={styles.brandPanel}>
          <div className={styles.brandInner}>
            <div className={styles.brandHead}>
              <img src="/logo.svg" alt={t('appName')} className={styles.brandLogo} />
              <div>
                <div className={styles.brandName}>{t('appName')}</div>
                <div className={styles.brandTagline}>{t('appTagline')}</div>
              </div>
            </div>

            <ul className={styles.featureList}>
              <li className={styles.featureItem}>
                <span className={styles.featureCheck}>
                  <Icon name="fa-check" size="sm" strokeWidth={2.5} />
                </span>
                {t('loginFeatureReceipts')}
              </li>
              <li className={styles.featureItem}>
                <span className={styles.featureCheck}>
                  <Icon name="fa-check" size="sm" strokeWidth={2.5} />
                </span>
                {t('loginFeatureBudget')}
              </li>
              <li className={styles.featureItem}>
                <span className={styles.featureCheck}>
                  <Icon name="fa-check" size="sm" strokeWidth={2.5} />
                </span>
                {t('loginFeatureInsights')}
              </li>
            </ul>
          </div>
        </aside>

        <main className={styles.formPanel}>
          <div className={styles.formCard}>
            <h1 className={styles.title}>{t('login')}</h1>

            {error && (
              <div className={styles.error} role="alert">
                <Icon name="fa-info-circle" size="sm" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.field}>
                <label htmlFor="username" className={styles.label}>{t('username')}</label>
                <input
                  type="text"
                  className={styles.input}
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  required
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="password" className={styles.label}>{t('password')}</label>
                <MaskedInput
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={isLoading}
                disabled={isLoading}
                className={styles.submit}
              >
                {isLoading ? t('loggingIn') : t('loginButton')}
              </Button>
            </form>

            <div className={styles.divider}>
              <span>{t('or')}</span>
            </div>

            <a
              href={`${process.env.REACT_APP_API_URL}/oauth2/authorization/google`}
              className={styles.googleBtn}
              onClick={() => {
                if (from !== '/dashboard') {
                  sessionStorage.setItem('post_auth_redirect', from);
                }
              }}
            >
              <img src="images/google.svg" alt="" className={styles.googleLogo} />
              <span>{t('continueWithGoogle')}</span>
            </a>

            <p className={styles.altLink}>
              {t('dontHaveAnAccount')}{' '}
              <Link to="/signup">{t('signup')}</Link>
            </p>
            <p className={styles.legalLink}>
              <Link to="/privacy">{t('privacy.link')}</Link>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
